import { useEffect, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import clsx from 'clsx';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import * as R from 'ramda';

import { useNavigation } from '@atom/hooks/useNavigation';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { WorkCollectionItem } from '@atom/types/work';
import api from '@atom/utilities/api';
import { WORK_ORDERS_ENDPOINT } from '@atom/utilities/endpoints';
import { getWorkOrderStatusCalendarColor } from '@atom/utilities/workOrderStatusUtilities';

import ScheduleCalendarEvent from './ScheduleCalendarEvent';
import ScheduleCalendarToolbar from './ScheduleCalendarToolbar';

import classes from './schedule.module.css';

const styles = {
  calendar: {
    height: 'calc(100vh - 60px)',
  },
  event: {
    color: colors.neutral.dim,
    fontSize: fonts.sm,
    borderRadius: '2px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
  },
};

interface CalendarFilters {
  calendarEnd: number;
  calendarStart: number;
}

interface WorkOrdersDictionary {
  [key: string]: WorkCollectionItem;
}

const getCalendarFilters = (view: string, viewDate: any): CalendarFilters => {
  switch (view) {
    case 'month': {
      return {
        calendarStart: startOfMonth(viewDate).valueOf(),
        calendarEnd: endOfMonth(viewDate).valueOf(),
      };
    }
    case 'week': {
      return {
        calendarStart: startOfWeek(viewDate).valueOf(),
        calendarEnd: endOfWeek(viewDate).valueOf(),
      };
    }
    case 'day': {
      return {
        calendarStart: startOfDay(viewDate).valueOf(),
        calendarEnd: endOfDay(viewDate).valueOf(),
      };
    }
    default: {
      return null;
    }
  }
};

// Needed for react-big-calendar
// https://github.com/jquense/react-big-calendar/blob/master/README.md
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const ScheduleCalendar = () => {
  const [, { navigate }] = useNavigation();
  const [loading, setLoading] = useState<boolean>(false);
  const [workOrdersDictionary, setWorkOrdersDictionary] =
    useState<WorkOrdersDictionary>({});
  const [projectedDictionary, setProjectedDictionary] =
    useState<WorkOrdersDictionary>({});
  const [showPmOnly, setShowPmOnly] = useState<boolean>(false);
  const [showProjectedPms, setShowProjectedPms] = useState<boolean>(false);

  const [viewDate, setViewDate] = useState<any>(new Date());
  const [view, setView] = useState<string>('month');

  const [calendarFilters, setCalendarFilters] = useState<CalendarFilters>(
    getCalendarFilters(view, viewDate),
  );

  useEffect(() => {
    setCalendarFilters(getCalendarFilters(view, viewDate));
  }, [view, viewDate]);

  useEffect(() => {
    const getWorkOrders = async () => {
      setLoading(true);
      const params = {
        page: 1,
        limit: 250,
        sortBy: 'name',
        isAscending: true,
        dueDateStart: calendarFilters.calendarStart,
        dueDateEnd: calendarFilters.calendarEnd,
      };

      const { data } = await api.get(WORK_ORDERS_ENDPOINT, params);

      const dictionary = data.reduce((acc, workOrder) => {
        return { ...acc, [workOrder.id]: workOrder };
      }, {});

      setWorkOrdersDictionary(dictionary);
      setLoading(false);
    };

    getWorkOrders();
  }, [calendarFilters.calendarStart, calendarFilters.calendarEnd]);

  // Forecast overlay: project PMs into the visible window when toggled on.
  useEffect(() => {
    if (!showProjectedPms) {
      setProjectedDictionary({});
      return;
    }
    const fetchForecast = async () => {
      try {
        const res = await fetch('/api/v1/pm/forecast?days=60');
        const data = await res.json();
        const dict: WorkOrdersDictionary = (data ?? []).reduce(
          (acc: WorkOrdersDictionary, entry: any) => {
            // De-dup against actual WOs by (templateId, assetId, week).
            const id = `proj-${entry.templateId}-${entry.assetId}-${entry.dueDate}`;
            acc[id] = {
              id,
              name: `${entry.templateName} — ${entry.assetName}`,
              dueDate: entry.dueDate,
              statusId: '1',
              priorityId: 2,
              isClosed: false,
              tasks: [],
              inventoryAssetName: entry.assetName,
              location: null as any,
              // Tagging for the event renderer + style getter.
              source: 'PM',
              isPmProjection: true,
              superseded: entry.superseded,
            } as any;
            return acc;
          },
          {},
        );
        setProjectedDictionary(dict);
      } catch (e) {
        setProjectedDictionary({});
      }
    };
    fetchForecast();
  }, [showProjectedPms, calendarFilters.calendarStart, calendarFilters.calendarEnd]);

  const mergedDictionary: WorkOrdersDictionary = {
    ...workOrdersDictionary,
    ...projectedDictionary,
  };

  const events = R.keys(mergedDictionary)
    .filter((workOrderId: string) => {
      const wo = mergedDictionary[workOrderId] as any;
      if (showPmOnly && wo?.source !== 'PM') return false;
      return true;
    })
    .map((workOrderId: string) => ({
      start: new Date(mergedDictionary[workOrderId].dueDate),
      end: new Date(mergedDictionary[workOrderId].dueDate),
      allDay: true,
      title: mergedDictionary[workOrderId].id,
    }));

  const onSelectEvent = event => {
    const wo = mergedDictionary[event.title] as any;
    if (wo?.isPmProjection) return; // Projections are read-only ghosts.
    navigate(`/workOrders/${event.title}`);
  };

  const getEventStyles = event => {
    const workOrder = mergedDictionary[event.title] as any;
    const isProjection = workOrder?.isPmProjection;
    const isPm = workOrder?.source === 'PM';
    // PM-source WOs use the existing requested status palette
    // (purple in the brand) so they sit alongside other WO statuses
    // without introducing a new color.
    const backgroundColor = isProjection
      ? 'var(--calendarRequested)'
      : isPm
        ? 'var(--requested)'
        : getWorkOrderStatusCalendarColor(workOrder.statusId);

    return {
      style: {
        ...styles.event,
        backgroundColor,
        color: isProjection
          ? 'var(--requested)'
          : isPm
            ? colors.neutral.white
            : undefined,
        opacity: isProjection ? 0.9 : 1,
        border: isProjection ? '1px dashed var(--requested)' : undefined,
      },
    };
  };

  return (
    <div className={clsx(classes.pane, classes.right)}>
      <Calendar
        localizer={localizer}
        defaultDate={viewDate}
        defaultView={view}
        events={events}
        startAccessor="start"
        endAccessor="end"
        onSelectEvent={onSelectEvent}
        style={styles.calendar}
        components={{
          toolbar: props => (
            <ScheduleCalendarToolbar
              {...props}
              setViewDate={setViewDate}
              setView={setView}
              loading={loading}
              showPmOnly={showPmOnly}
              showProjectedPms={showProjectedPms}
              onTogglePmOnly={() => setShowPmOnly(v => !v)}
              onToggleProjected={() => setShowProjectedPms(v => !v)}
            />
          ),
          event: props => (
            <ScheduleCalendarEvent
              {...props}
              workOrdersDictionary={mergedDictionary as any}
            />
          ),
        }}
        eventPropGetter={getEventStyles}
      />
    </div>
  );
};

export default ScheduleCalendar;
